/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import * as codec from 'purify-ts/Codec';
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { parsers } from '@codegrade/utils';
import { AssertionError, keysOfSlow } from '@/utils';
import { BaseAutoTestStep } from './base';
import { errOrData, mergeEithers } from './utils';
// Be careful when changing these options, as they have to be
// backwards compatible, as otherwise users will lose their
// configuration if they edit the suite.
const junit5MetadataCodec = codec.Codec.interface({
    javaArguments: codec.string,
    testClasses: codec.string,
    junitArguments: codec.string,
});
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const pytestCaptureOutputModes = Object.freeze([
    'no',
    'log',
    'system-out',
    'system-err',
    'out-err',
    'all',
]);
const pytestMetadataCodec = codec.Codec.interface({
    filesToTest: codec.string,
    captureOutput: parsers.maybe(codec.exactly(...pytestCaptureOutputModes)),
    captureOutputPassing: codec.boolean,
    setPythonPath: codec.boolean,
    pytestArguments: codec.string,
});
const genericMetadataCodec = codec.Codec.interface({
    args: codec.optional(codec.string),
});
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function metadataCodecWithGenericFallback(baseCodec, fromGeneric) {
    return codec.Codec.custom({
        decode: (value) => baseCodec
            .decode(value)
            .chainLeft(err => genericMetadataCodec.decode(value).bimap(() => err, fromGeneric)),
        encode: baseCodec.encode,
    });
}
const phpunitMetadataCodec = metadataCodecWithGenericFallback(codec.Codec.interface({
    filesToTest: codec.string,
    phpunitExecutable: parsers.maybe(codec.string),
    phpunitArguments: codec.string,
}), ({ args }) => ({
    filesToTest: '',
    phpunitExecutable: Maybe.empty(),
    phpunitArguments: args ?? '',
}));
const xunitMetadataCodec = metadataCodecWithGenericFallback(codec.Codec.interface({
    testProject: codec.string,
    xunitArguments: codec.string,
}), ({ args }) => ({
    testProject: '',
    xunitArguments: args ?? '',
}));
const pytestParser = codec.Codec.interface({
    wrapper: codec.exactly('cg_pytest'),
    metadata: pytestMetadataCodec,
});
const junit5Parser = codec.Codec.interface({
    wrapper: codec.exactly('cg_junit5'),
    metadata: junit5MetadataCodec,
});
const phpunitParser = codec.Codec.interface({
    wrapper: codec.exactly('cg_phpunit'),
    metadata: phpunitMetadataCodec,
});
const xunitParser = codec.Codec.interface({
    wrapper: codec.exactly('cg_xunit'),
    metadata: xunitMetadataCodec,
});
const genericParser = codec.Codec.interface({
    wrapper: codec.optional(codec.exactly('custom', 'cg_check', 'cg_junit4', 'cg_jest', 'cg_moxunit', 'cg_mocha', 'cg_quickcheck', 'cg_semgrep')),
    metadata: codec.optional(genericMetadataCodec),
});
const wrapperAndMetadataParser = codec.oneOf([
    pytestParser,
    junit5Parser,
    phpunitParser,
    xunitParser,
    genericParser,
]);
const dataParser = codec.intersect(api.JunitTestBaseData, wrapperAndMetadataParser);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class UnitTestAutoTestStep extends BaseAutoTestStep {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    parseInputData(data) {
        return Maybe.fromEither(dataParser.decode(data))
            .filter(res => res.metadata != null)
            .orDefaultLazy(() => Maybe.fromFalsy(data.program).caseOf({
            Just: program => UnitTestAutoTestStep.getDefaultData('custom', program),
            Nothing: () => UnitTestAutoTestStep.getDefaultData(undefined, ''),
        }));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static getDefaultData(wrapper, program) {
        switch (wrapper) {
            case 'cg_junit5':
                return {
                    wrapper,
                    metadata: {
                        javaArguments: '',
                        junitArguments: '',
                        testClasses: '',
                    },
                    program,
                };
            case 'cg_pytest':
                return {
                    wrapper,
                    metadata: {
                        filesToTest: '',
                        captureOutput: Maybe.empty(),
                        captureOutputPassing: false,
                        setPythonPath: true,
                        pytestArguments: '',
                    },
                    program,
                };
            case 'cg_phpunit':
                return {
                    wrapper,
                    metadata: {
                        filesToTest: '',
                        phpunitExecutable: Maybe.empty(),
                        phpunitArguments: '',
                    },
                    program,
                };
            case 'cg_xunit':
                return {
                    wrapper,
                    metadata: {
                        testProject: '',
                        xunitArguments: '',
                    },
                    program,
                };
            default:
                return {
                    wrapper,
                    metadata: undefined,
                    program,
                };
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static stepDataEquals(a, b) {
        switch (a.wrapper) {
            case undefined:
                return a.program.trim() === b.program.trim();
            case 'custom':
            case 'cg_check':
            case 'cg_junit4':
            case 'cg_jest':
            case 'cg_moxunit':
            case 'cg_mocha':
            case 'cg_quickcheck':
            case 'cg_semgrep': {
                if (a.wrapper !== b.wrapper) {
                    return false;
                }
                if (a.program.trim() !== b.program.trim()) {
                    return false;
                }
                const metadataA = a.metadata;
                const metadataB = b.metadata;
                if (metadataA == null || metadataB == null) {
                    return (metadataA == null) === (metadataB == null);
                }
                return keysOfSlow(['args']).every(key => {
                    switch (key) {
                        case 'args':
                            return metadataA[key]?.trim() === metadataB[key]?.trim();
                        default:
                            return AssertionError.assertNever(key);
                    }
                });
            }
            case 'cg_pytest': {
                if (a.wrapper !== b.wrapper) {
                    return false;
                }
                return keysOfSlow([
                    'pytestArguments',
                    'filesToTest',
                    'captureOutput',
                    'captureOutputPassing',
                    'setPythonPath',
                ]).every(key => {
                    switch (key) {
                        case 'pytestArguments':
                        case 'filesToTest':
                            return a.metadata[key].trim() === b.metadata[key].trim();
                        case 'captureOutput':
                            return a.metadata[key].equals(b.metadata[key]);
                        case 'captureOutputPassing':
                        case 'setPythonPath':
                            return a.metadata[key] === b.metadata[key];
                        default:
                            return AssertionError.assertNever(key);
                    }
                });
            }
            case 'cg_junit5': {
                if (a.wrapper !== b.wrapper) {
                    return false;
                }
                return keysOfSlow([
                    'javaArguments',
                    'testClasses',
                    'junitArguments',
                ]).every(key => {
                    switch (key) {
                        case 'javaArguments':
                        case 'testClasses':
                        case 'junitArguments':
                            return a.metadata[key].trim() === b.metadata[key].trim();
                        default:
                            return AssertionError.assertNever(key);
                    }
                });
            }
            case 'cg_phpunit': {
                if (a.wrapper !== b.wrapper) {
                    return false;
                }
                return keysOfSlow([
                    'filesToTest',
                    'phpunitExecutable',
                    'phpunitArguments',
                ]).every(key => {
                    switch (key) {
                        case 'filesToTest':
                        case 'phpunitArguments':
                            return a.metadata[key].trim() === b.metadata[key].trim();
                        case 'phpunitExecutable':
                            return a.metadata[key].equals(b.metadata[key]);
                        default:
                            return AssertionError.assertNever(key);
                    }
                });
            }
            case 'cg_xunit': {
                if (a.wrapper !== b.wrapper) {
                    return false;
                }
                return keysOfSlow(['testProject', 'xunitArguments']).every(key => {
                    switch (key) {
                        case 'testProject':
                        case 'xunitArguments':
                            return a.metadata[key] === b.metadata[key];
                        default:
                            return AssertionError.assertNever(key);
                    }
                });
            }
            default:
                return AssertionError.assertNever(a);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getDataToSave() {
        const errs = this._checkProgramNotEmpty();
        switch (this.data.wrapper) {
            case 'cg_junit5': {
                const testClasses = this.data.metadata.testClasses ?? '';
                if (testClasses.trim() === '') {
                    errs.push('At least one test class must be present.');
                }
                break;
            }
            case 'cg_pytest': {
                const filesToTest = this.data.metadata?.filesToTest ?? '';
                if (filesToTest.trim() === '') {
                    errs.push('At least one test file must be present.');
                }
                break;
            }
            case 'cg_phpunit': {
                const filesToTest = this.data.metadata?.filesToTest ?? '';
                if (filesToTest.trim() === '') {
                    errs.push('At least one test file must be present.');
                }
                break;
            }
            case 'cg_xunit': {
                const testProject = this.data.metadata?.testProject ?? '';
                if (testProject.trim() === '') {
                    errs.push('A test project must be present.');
                }
                break;
            }
            default:
                break;
        }
        const outputData = api.JunitTestData.unsafeDecode(dataParser.encode(this.data));
        return mergeEithers(this._baseGetDataToSave(), errOrData(errs, {
            data: outputData,
            type: this.type,
        }));
    }
}
